import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Eye, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { issueNoteService } from '../../services/issueNoteService';
import { IssueNoteListDTO, IssueNoteStatus } from '../../types';
import './IssueNotePage.css';

const HISTORY_STATUSES: IssueNoteStatus[] = ['Posted', 'Cancelled'];

const STATUS_CLASS: Record<IssueNoteStatus, string> = {
  Draft:           'ims-badge isn-badge-draft',
  PendingApproval: 'ims-badge isn-badge-pending',
  Approved:        'ims-badge isn-badge-approved',
  Rejected:        'ims-badge isn-badge-rejected',
  Posted:          'ims-badge isn-badge-posted',
  Cancelled:       'ims-badge isn-badge-cancelled',
};

const STATUS_LABEL: Record<IssueNoteStatus, string> = {
  Draft:           'Draft',
  PendingApproval: 'Pending Approval',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
};

const IssueNoteHistoryPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [items,      setItems]      = useState<IssueNoteListDTO[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => HISTORY_STATUSES.includes(n.status)));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => HISTORY_STATUSES.includes(n.status)));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const postedCount    = items.filter(n => n.status === 'Posted').length;
  const cancelledCount = items.filter(n => n.status === 'Cancelled').length;

  const columns: GridColumn[] = [
    {
      key: 'issueNoteNumber',
      label: t('issueNote.issueNoteNumber', 'ISN Number'),
      sortable: true,
      width: '160px',
      render: (v) => <code>{v as string}</code>,
    },
    {
      key: 'issueDate',
      label: t('issueNote.issueDate', 'Issue Date'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    {
      key: 'warehouseName',
      label: t('issueNote.warehouse', 'Warehouse'),
      width: '160px',
    },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      sortable: true,
      width: '130px',
      render: (v) => {
        const s = v as IssueNoteStatus;
        return <span className={STATUS_CLASS[s] ?? 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    {
      key: 'itemCount',
      label: t('issueNote.items', 'Items'),
      width: '80px',
    },
    {
      key: 'issuedByName',
      label: t('issueNote.issuedBy', 'Issued By'),
      width: '140px',
    },
    {
      key: 'createdAt',
      label: t('common.createdAt', 'Created'),
      sortable: true,
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('common.view', 'View'),
      onClick: (row) => navigate(`/issue-notes/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="isn-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('issueNote.history', 'Issue Note History')}</h1>
            <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, marginTop: 2 }}>
              {t('issueNote.historyDesc', 'Completed (posted) and cancelled issue notes')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh', 'Refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh', 'Refresh')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/issue-notes')}>
              {t('issueNote.backToList', 'Back to List')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-posted">
              <CheckCircle2 size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{postedCount}</span>
              <span className="ims-stat-label">{t('issueNote.posted', 'Posted')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-draft">
              <XCircle size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{cancelledCount}</span>
              <span className="ims-stat-label">{t('issueNote.cancelled', 'Cancelled')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={items}
            loading={loading}
            actions={actions}
            pageSize={10}
            searchPlaceholder={t('common.search', 'Search…')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={t('issueNote.noHistory', 'No posted or cancelled issue notes found')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default IssueNoteHistoryPage;
